/**
 * THE PORTFOLIO ENDPOINT'S CLIENT, and the formatting its panel needs.
 *
 * <p>Split from the component for the reason `replayDiagnostics.js` is: jsdom cannot render the
 * dialog without mocking MUI and the API away, and every property worth asserting here is about
 * CONTENT — that an absent funding list and an empty one build DIFFERENT queries, that an
 * undefined ratio renders as absent rather than as a number.
 */

/** Every strategy the endpoint can replay. Mirrors `PortfolioService.KNOWN_STRATEGIES`. */
export const PORTFOLIO_STRATEGIES = ['TRENDPULSE', 'CTS'];

/** The symbols offered by default. Both have the daily history the endpoint replays. */
export const PORTFOLIO_SYMBOLS = ['SPY', 'QQQ'];

/** The account the panel opens with — the figures every published net result was measured under. */
export const PORTFOLIO_DEFAULT_ACCOUNT = Object.freeze({
  capital: 1000000,
  riskPerTradePct: 1.0,
  commissionPerShare: 0.005,
  slippageBps: 5,
});

/** Every key a symbol/strategy selection implies, in a stable order. */
export const keysFor = (symbols, strategies) => {
  const out = [];
  (symbols || []).forEach((symbol) => {
    (strategies || []).forEach((strategy) => out.push(`${strategy}@${symbol}`));
  });
  return out;
};

/**
 * The query string for a run.
 *
 * <h2>An absent funding list and an EMPTY one are different requests</h2>
 *
 * Omitting `fundKeys` means "no filter" — fund everything replayed. Sending it empty means "fund
 * nothing", which is a legitimate ask: it prices every signal without backing any of them, and
 * each strategy's paper curve still accrues. If the two collapsed, clearing every checkbox would
 * silently produce a fully-funded book — the opposite of what the click meant.
 *
 * <p>So `fundKeys: null` omits the parameter and `fundKeys: []` sends it blank.
 */
export const buildPortfolioQuery = ({
  symbols, strategies, fundKeys,
  capital, riskPerTradePct, commissionPerShare, slippageBps,
  maxGrossExposurePct, maxPerPositionPct, maxPortfolioRiskPct,
} = {}) => {
  const params = new URLSearchParams();
  if (symbols && symbols.length) params.set('symbols', symbols.join(','));
  if (strategies && strategies.length) params.set('strategies', strategies.join(','));
  if (Array.isArray(fundKeys)) params.set('fundKeys', fundKeys.join(','));

  // Sent when set, including ZERO for the cost levers — "no commission" is how a frictionless
  // comparison is asked for, and a falsy check would drop it and restore the default.
  if (capital) params.set('capital', String(capital));
  if (riskPerTradePct) params.set('riskPerTradePct', String(riskPerTradePct));
  if (commissionPerShare !== undefined && commissionPerShare !== null) {
    params.set('commissionPerShare', String(commissionPerShare));
  }
  if (slippageBps !== undefined && slippageBps !== null) {
    params.set('slippageBps', String(slippageBps));
  }
  if (maxGrossExposurePct) params.set('maxGrossExposurePct', String(maxGrossExposurePct));
  if (maxPerPositionPct) params.set('maxPerPositionPct', String(maxPerPositionPct));
  if (maxPortfolioRiskPct) params.set('maxPortfolioRiskPct', String(maxPortfolioRiskPct));
  return params.toString();
};

/** Runs the portfolio and resolves with the response. */
export const runPortfolio = async (api, options) => {
  const { data } = await api.get(`/api/portfolio/run?${buildPortfolioQuery(options)}`);
  return data;
};

/* ── formatting ──────────────────────────────────────────────────────────────────────────────── */

export const fmtCurrency = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const sign = value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

export const fmtPct = (value, places = 2) =>
  (value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : `${value >= 0 ? '+' : ''}${value.toFixed(places)}%`);

/**
 * A ratio, or `n/a`.
 *
 * <p>NEVER a large number in place of an absent one. A profit factor with no losing trade has no
 * denominator and a return/drawdown with no drawdown has none either — both are absent readings,
 * not perfect scores, and the engine's own `LedgerStatistics` makes the same call. A UI showing
 * "Infinity" beside a return would read as a spectacular result rather than an undefined one.
 */
export const fmtRatio = (value, places = 2) =>
  (value === null || value === undefined || !Number.isFinite(value)
    ? 'n/a'
    : value.toFixed(places));

/**
 * Which budget is actually binding, as a sentence.
 *
 * <p>This is the panel's most useful line and the reason the two decline counts are never summed.
 * A tight stop turns a 1% risk budget into a position averaging 75–85% of equity, so the book runs
 * out of CASH long before it runs out of risk appetite — across 585 measured signals the 6% risk
 * ceiling refused exactly zero. A reader shown one "declined" number would raise the risk ceiling,
 * see nothing change, and raise it again.
 */
export const bindingConstraint = (result) => {
  if (!result || !result.signalsSeen) return null;
  const { declinedExposure = 0, declinedRisk = 0 } = result;
  if (declinedExposure > 0 && declinedRisk === 0) {
    return 'CASH is binding. The risk ceiling refused nothing, so raising it changes nothing — '
      + 'per-trade risk or more capital is the lever.';
  }
  if (declinedRisk > 0 && declinedExposure === 0) {
    return 'RISK is binding — unusual here, and it means the stop distance has widened enough '
      + 'that positions no longer exhaust the cash first.';
  }
  if (declinedRisk > 0 && declinedExposure > 0) {
    return 'Both budgets refused signals, so neither alone explains the shortfall.';
  }
  return 'Neither budget refused a signal: every funded key got every trade it asked for.';
};
